export function findScrollParent(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
    }
    return null;
}

function isNearBottom(scrollEl: HTMLElement): boolean {
    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    return (scrollHeight - scrollTop - clientHeight) / clientHeight < 0.1;
}

/**
 * Scrolls the nearest scroll ancestor to the bottom — but only if the user is already
 * near the bottom (within 10% of client height). Useful when appending content so the
 * user isn't unexpectedly jumped away from their position.
 */
export function scrollToBottomIfNear(el: HTMLElement): void {
    const scrollEl = findScrollParent(el);
    if (!scrollEl || !isNearBottom(scrollEl)) return;

    const observer = new ResizeObserver(() => {
        scrollEl.scrollTop = scrollEl.scrollHeight;
    });
    observer.observe(el);
    setTimeout(() => observer.disconnect(), 300);
}

/**
 * Scrolls the nearest scroll ancestor to keep the element's bottom edge in view as it
 * expands. Call just before triggering the expand so the ResizeObserver is in place
 * before the height animation starts.
 *
 * @param el - the expanding element
 * @param disconnectDelay - ms after which the observer is torn down; should match the
 *   expand animation duration (default 260 covers the built-in 250 ms CSS transition)
 */
export function scrollFollowOnExpand(el: HTMLElement, disconnectDelay = 260): void {
    const scrollEl = findScrollParent(el);
    if (!scrollEl) return;

    const observer = new ResizeObserver(() => {
        const overshoot = el.getBoundingClientRect().bottom - scrollEl.getBoundingClientRect().bottom;
        if (overshoot > 0) scrollEl.scrollTop += overshoot;
    });
    observer.observe(el);
    setTimeout(() => observer.disconnect(), disconnectDelay);
}
